import { useNavigate } from "@tanstack/react-router";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTabsActions } from "@/contexts/tabs-context";
import { useCommandPaletteResults } from "../hooks/use-command-palette-results";
import {
  getCommandPaletteShortcutKeys,
  getCommandPaletteShortcutLabel,
  isCommandPaletteShortcut,
} from "../lib/shortcut";
import type { CommandPaletteResult } from "../types";
import { CommandPaletteDialog } from "./command-palette-dialog";

interface CommandPaletteContextValue {
  openPalette: () => void;
  closePalette: () => void;
  shortcutKeys: readonly ["Cmd" | "Ctrl", "K"];
  shortcutLabel: string;
}

const globalCommandPaletteContextKey = "__df_command_palette_context__";
const globalCommandPaletteWarningKey = "__df_command_palette_missing_provider_warned__";

type CommandPaletteGlobalStore = typeof globalThis & {
  [globalCommandPaletteContextKey]?: React.Context<CommandPaletteContextValue | null>;
  [globalCommandPaletteWarningKey]?: boolean;
};

const commandPaletteGlobalStore = globalThis as CommandPaletteGlobalStore;
const CommandPaletteContext =
  commandPaletteGlobalStore[globalCommandPaletteContextKey] ??
  createContext<CommandPaletteContextValue | null>(null);
commandPaletteGlobalStore[globalCommandPaletteContextKey] = CommandPaletteContext;

const commandPaletteDevFallbackContext: CommandPaletteContextValue = {
  openPalette: () => {},
  closePalette: () => {},
  shortcutKeys: getCommandPaletteShortcutKeys(),
  shortcutLabel: getCommandPaletteShortcutLabel(),
};
const isDevEnvironment = process.env.NODE_ENV !== "production";

export function CommandPaletteProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { openProjectTab } = useTabsActions();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const { scope, isLoading, results } = useCommandPaletteResults(query);

  const openPalette = useCallback(() => {
    setOpen(true);
  }, []);

  const closePalette = useCallback(() => {
    setOpen(false);
  }, []);

  const handleSelectResult = useCallback(
    (result: CommandPaletteResult) => {
      setOpen(false);
      setQuery("");

      if (result.action.type === "openProject") {
        openProjectTab({ id: result.action.projectSlug, name: result.action.projectName });
        navigate({ to: `/project/${result.action.projectSlug}` });
        return;
      }

      openProjectTab({ id: result.action.projectSlug, name: result.action.projectName });
      navigate({ to: `/project/${result.action.projectSlug}${result.action.path}` });
    },
    [navigate, openProjectTab]
  );

  useEffect(() => {
    if (open) {
      return;
    }

    setQuery("");
  }, [open]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isCommandPaletteShortcut(event) || event.repeat) {
        return;
      }

      if (!window.matchMedia("(min-width: 768px)").matches) {
        return;
      }

      event.preventDefault();
      setOpen(true);
    };

    document.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => document.removeEventListener("keydown", handleKeyDown, { capture: true });
  }, []);

  const contextValue = useMemo<CommandPaletteContextValue>(
    () => ({
      openPalette,
      closePalette,
      shortcutKeys: getCommandPaletteShortcutKeys(),
      shortcutLabel: getCommandPaletteShortcutLabel(),
    }),
    [closePalette, openPalette]
  );

  return (
    <CommandPaletteContext.Provider value={contextValue}>
      {children}
      <CommandPaletteDialog
        open={open}
        scope={scope}
        query={query}
        isLoading={isLoading}
        results={results}
        onOpenChange={setOpen}
        onQueryChange={setQuery}
        onSelectResult={handleSelectResult}
      />
    </CommandPaletteContext.Provider>
  );
}

export function useCommandPalette() {
  const context = useContext(CommandPaletteContext);

  if (!context && isDevEnvironment) {
    if (!commandPaletteGlobalStore[globalCommandPaletteWarningKey]) {
      // Fast Refresh can briefly desync provider/consumer trees.
      // Avoid crashing during HMR and log once to aid diagnosis.
      console.warn("useCommandPalette resolved before CommandPaletteProvider during HMR.");
      commandPaletteGlobalStore[globalCommandPaletteWarningKey] = true;
    }
    return commandPaletteDevFallbackContext;
  }

  if (!context) {
    throw new Error("useCommandPalette must be used within a CommandPaletteProvider");
  }

  return context;
}
